import { Component } from '@theme/component';

/** Arrow enhancement for a native scrolling rail; touch and keyboard work without JS. */
class HenryBirthdayScroller extends Component {
  #observer = new ResizeObserver(() => this.#queueControls());
  #controller;
  #controlsFrame;

  connectedCallback() {
    super.connectedCallback();
    this.#observe();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer.disconnect();
    this.#controller?.abort();
    cancelAnimationFrame(this.#controlsFrame);
    this.#controlsFrame = undefined;
  }

  #observe() {
    this.#observer.disconnect();
    this.#controller?.abort();
    cancelAnimationFrame(this.#controlsFrame);
    this.#controlsFrame = undefined;
    this.#controller = new AbortController();
    if (this.refs.rail) {
      this.#observer.observe(this.refs.rail);
      this.refs.rail.addEventListener('scroll', () => this.#queueControls(), {
        passive: true,
        signal: this.#controller.signal,
      });
    }
    this.updateControls();
  }

  #queueControls() {
    if (this.#controlsFrame !== undefined) return;
    this.#controlsFrame = requestAnimationFrame(() => {
      this.#controlsFrame = undefined;
      if (this.isConnected) this.updateControls();
    });
  }

  updateControls() {
    const { rail, controls, previous, next } = this.refs;
    if (!rail || !controls || !previous || !next) return;
    const maximum = rail.scrollWidth - rail.clientWidth;
    const position = Math.abs(rail.scrollLeft);
    const hidden = maximum <= 2;
    const atStart = position <= 2;
    const atEnd = position >= maximum - 2;
    if (controls.hidden !== hidden) controls.hidden = hidden;
    if (previous.disabled !== atStart) previous.disabled = atStart;
    if (next.disabled !== atEnd) next.disabled = atEnd;
  }

  previous() {
    this.#move(-1);
  }

  next() {
    this.#move(1);
  }

  /** Keep keyboard navigation aligned with whole mosaic panels. */
  onKeydown(event) {
    if (event.target !== this.refs.rail || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const rtl = getComputedStyle(this.refs.rail).direction === 'rtl';
    this.#move((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
  }

  #move(direction) {
    const { rail } = this.refs;
    if (!rail) return;
    const rtl = getComputedStyle(rail).direction === 'rtl';
    const bounds = rail.getBoundingClientRect();
    const position = Math.abs(rail.scrollLeft);
    const maximum = rail.scrollWidth - rail.clientWidth;
    const padding = parseFloat(getComputedStyle(rail).paddingInlineStart) || 0;
    const points = Array.from(rail.children, (card) => {
      const box = card.getBoundingClientRect();
      return Math.min(maximum, Math.max(0, position + (rtl ? bounds.right - box.right : box.left - bounds.left) - padding));
    });
    const target = direction > 0
      ? points.find((point) => point > position + 2) ?? maximum
      : points.reverse().find((point) => point < position - 2) ?? 0;
    rail.scrollTo({
      left: target * (rtl ? -1 : 1),
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  }
}

if (!customElements.get('henry-birthday-scroller')) {
  customElements.define('henry-birthday-scroller', HenryBirthdayScroller);
}
